import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, AlertCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function BrokerForm() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const [responderName, setResponderName] = useState("");
  const [responderRole, setResponderRole] = useState("");
  const [responderEmail, setResponderEmail] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/respond/${token}`).then(r => setMeta(r.data)).catch(e => setErr(formatApiError(e.response?.data?.detail) || "Token inválido"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!outcome) { toast.error("Selecciona una opción"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/public/respond/${token}`, {
        outcome, reason: outcome === "refused" ? reason : null,
        responder_name: responderName, responder_role: responderRole, responder_email: responderEmail,
      });
      setSubmitted(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8] p-6">
        <div className="max-w-md w-full bg-white border border-red-200 p-8 text-center">
          <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
          <h1 className="font-display text-xl font-bold mb-2">No pudimos validar este enlace</h1>
          <p className="text-sm text-gray-600">{err}</p>
        </div>
      </div>
    );
  }
  if (!meta) return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-gray-500">Cargando…</div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white border border-green-300 p-10 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold">Respuesta registrada</h1>
          <p className="mt-3 text-gray-600">Hemos registrado su respuesta oficial con un sello de trazabilidad legal.</p>
          <div className="mt-6 bg-gray-50 border border-gray-200 p-4 font-mono text-xs text-left">
            <div className="text-gray-500 mb-1">Hash de respuesta:</div>
            <div className="break-all">{submitted.hash}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Slim institutional header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={26} />
            <div className="font-display font-bold text-gray-900">
              borra-me<span className="text-[#DC2626]">.</span>com
            </div>
            <span className="text-xs text-gray-400 font-mono">· Portal de respuesta para Responsables de Datos</span>
          </div>
          <div className="text-xs font-mono text-gray-500 hidden sm:flex items-center gap-1"><Lock className="w-3 h-3" /> Conexión cifrada</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="eyebrow mb-3">Solicitud ARCOP — Ley 21.719</div>
        <h1 className="font-display text-3xl font-bold text-gray-900 leading-tight">
          Respuesta oficial para <span className="text-[#0B2447]">{meta.broker_name}</span>
        </h1>

        <div className="grid sm:grid-cols-3 gap-px bg-gray-200 border border-gray-200 mt-8">
          <KV label="Titular">{meta.titular || "—"}</KV>
          <KV label="RUT">{meta.rut || "—"}</KV>
          <KV label="Plazo legal de respuesta">{new Date(meta.deadline).toLocaleDateString("es-CL")}</KV>
        </div>

        {meta.responded ? (
          <div className="mt-10 bg-amber-50 border border-amber-300 p-6 text-amber-900 text-sm">
            Esta solicitud ya fue respondida y se encuentra cerrada. Si necesitas modificarla, contáctanos en soporte@borra-me.com.
          </div>
        ) : (
        <form onSubmit={submit} className="mt-10 bg-white border border-gray-300 p-8 space-y-6" data-testid="broker-form">
          <div>
            <div className="eyebrow mb-3">Resultado de esta solicitud</div>
            <div className="space-y-2">
              {(meta.entity_type === "empresa"
                ? [
                    { v: "oposicion_ok", t: "Confirmo la oposición al marketing y la supresión de los datos no obligatorios del titular" },
                    { v: "confirmed_deletion", t: "Confirmo la eliminación total (no había obligación legal de retención)" },
                    { v: "no_data", t: "No encontré datos de este titular en mis sistemas" },
                    { v: "refused", t: "No puedo ejecutar esta solicitud (debo indicar razón legal válida)" },
                  ]
                : [
                    { v: "confirmed_deletion", t: "Confirmé la eliminación de todos los datos del titular de mi base de datos" },
                    { v: "no_data", t: "No encontré datos de este titular en mis sistemas" },
                    { v: "refused", t: "No puedo eliminar estos datos (debo indicar razón legal válida)" },
                  ]
              ).map((o) => (
                <label key={o.v} className={`flex items-start gap-3 p-4 border ${outcome === o.v ? "border-[#0B2447] bg-[#0B2447]/5" : "border-gray-200"} cursor-pointer`} data-testid={`option-${o.v}`}>
                  <input type="radio" name="outcome" value={o.v} checked={outcome === o.v} onChange={(e) => setOutcome(e.target.value)} />
                  <span className="text-sm">{o.t}</span>
                </label>
              ))}
            </div>
          </div>

          {outcome === "refused" && (
            <div>
              <label className="eyebrow block mb-2">Razón legal válida</label>
              <textarea required value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="refusal-reason"
                className="w-full border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#0B2447] focus:border-transparent" />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Nombre de quien responde</label>
              <input required value={responderName} onChange={(e) => setResponderName(e.target.value)} data-testid="responder-name"
                className="w-full border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#0B2447] focus:border-transparent" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Cargo</label>
              <input required value={responderRole} onChange={(e) => setResponderRole(e.target.value)} data-testid="responder-role"
                className="w-full border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#0B2447] focus:border-transparent" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Email institucional</label>
            <input required type="email" value={responderEmail} onChange={(e) => setResponderEmail(e.target.value)} data-testid="responder-email"
              className="w-full border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#0B2447] focus:border-transparent" />
          </div>

          <button data-testid="broker-form-submit" disabled={loading} type="submit"
            className="w-full bg-[#0B2447] hover:bg-[#071932] text-white font-semibold py-3 transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            {loading ? "Enviando..." : "Enviar respuesta oficial"}
          </button>
          <p className="text-xs text-gray-500 text-center font-mono">Su respuesta queda registrada con IP, timestamp y hash criptográfico para trazabilidad legal.</p>
        </form>
        )}
      </div>
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div className="bg-white p-4">
      <div className="eyebrow mb-1">{label}</div>
      <div className="text-sm font-mono text-gray-900">{children}</div>
    </div>
  );
}
